/**
 * Export schemas — shared FE ↔ BE.
 *
 * Per DESIGN.md §5.7 (endpoints 47–52) + REQUIREMENTS.md FR-EXP-001..005.
 */
import { z } from 'zod';

export const EXPORT_TYPE_CODES = [
  'CUTTING_LIST_XLSX',
  'GLASS_LIST_XLSX',
  'CLIENT_QUOTE_PDF',
  'LAYOUT_PDF',
] as const;

export type ExportTypeCode = (typeof EXPORT_TYPE_CODES)[number];

export const CreateExportSchema = z
  .object({
    exportType: z.enum(EXPORT_TYPE_CODES),
    projectId: z.number().int().positive().optional(),
    runId: z.number().int().positive().optional(),
  })
  .refine((d) => d.projectId !== undefined || d.runId !== undefined, {
    message: 'At least one of projectId or runId is required',
  });

export type CreateExportInput = z.infer<typeof CreateExportSchema>;

export const ExportDtoSchema = z.object({
  id: z.number().int().positive(),
  tenantId: z.number().int().positive(),
  projectId: z.number().int().positive().nullable(),
  runId: z.number().int().positive().nullable(),
  exportType: z.enum(EXPORT_TYPE_CODES),
  s3Key: z.string(),
  fileSizeBytes: z.number().nullable(),
  contentHashSha256: z.string().nullable(),
  contentType: z.string().nullable(),
  createdDate: z.string().datetime(),
});

export type ExportDto = z.infer<typeof ExportDtoSchema>;

export const ListExportsQuerySchema = z.object({
  cursor: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  projectId: z.coerce.number().int().positive().optional(),
  exportType: z.enum(EXPORT_TYPE_CODES).optional(),
});

export type ListExportsQuery = z.infer<typeof ListExportsQuerySchema>;

export const PaginatedExportsDtoSchema = z.object({
  items: z.array(ExportDtoSchema),
  nextCursor: z.number().int().nullable(),
  hasMore: z.boolean(),
});

export type PaginatedExportsDto = z.infer<typeof PaginatedExportsDtoSchema>;
